import { Injectable, inject } from "@angular/core";
import { Tracker } from "../../../../common/libraries/analytics/tracker";
import { SignupEventData } from "../../../../common/interfaces/analytics";
import { SignUpEntryService } from "./entry.service";
import { SignUpShotCallerService } from "./shot-caller.service";
import { Step } from "./navigation.service";

const debug = false;

@Injectable({ providedIn: "root" })
export class SignupAnalyticsService {
  private entry = inject(SignUpEntryService);
  private shotCaller = inject(SignUpShotCallerService);

  private _touched!: boolean;
  private tracker: Tracker;
  private eventData: SignupEventData = {
    funnel_step: ""
  };
  private userIdSetOnce = false;

  constructor() {
    this.tracker = new Tracker("signup", {
      schemas: [
        {
          collector: "fivetran",
          name: "signup",
          schema: "https://app.onsip.com/analytics/SignupSchema-analytics-v1.0.7.json"
        },
        {
          name: "signup",
          schema: "iglu:com.onsip/signup/jsonschema/1-0-1",
          collector: "snowcat"
        }
      ]
    });
  }

  get touched(): boolean {
    return this._touched;
  }

  // represents whether or not a user has interacted with either the avatar or org configuration
  set touched(touched: boolean) {
    debug && console.warn("SignupAnalyticsService: set touched", touched);
    this._touched = touched;
  }

  // Only to be used by the navigation service
  sendAnalyticsEvent(funnelStep: Step, postSignup: boolean, cms?: string): void {
    debug && console.log("SignupNavigationService: sendAnalyticsEvent", funnelStep);

    const actionData: SignupEventData = {
      funnel_step: funnelStep,
      phone: this.entry.contactPhone,
      account_type: this.entry.accountType,
      account_plan: this.entry.accountPlan,
      name: postSignup ? this.shotCaller.name : this.entry.name,
      domain: postSignup ? this.shotCaller.domain : this.entry.domain,
      email: postSignup ? this.shotCaller.email : this.entry.email,
      organization_name: postSignup ? this.shotCaller.orgName : this.entry.orgName,
      username: postSignup ? this.shotCaller.username : this.entry.username,
      user_id: postSignup ? this.shotCaller.userId : undefined,
      organization_id: postSignup ? this.shotCaller.organizationId : undefined,
      account_id: postSignup ? this.shotCaller.accountId : undefined,
      content_management_system: cms,
      touched: this.touched
    };
    if (postSignup && !this.userIdSetOnce) {
      this.setUserId(this.shotCaller.userId);
    }
    this.trackEvent(actionData);
  }

  setUserId(userId: number): void {
    if (!this.userIdSetOnce) {
      this.tracker.setUserId(userId);
      this.userIdSetOnce = true;
    }
  }

  private trackEvent(actionData: SignupEventData) {
    debug && console.log("SignupAnalyticsService: trackEvent", actionData);

    this.eventData.funnel_step = actionData.funnel_step;
    if (actionData.email !== undefined) {
      this.eventData.email = actionData.email;
    }
    if (actionData.organization_name !== undefined) {
      this.eventData.organization_name = actionData.organization_name;
    }
    if (actionData.account_plan !== undefined) {
      this.eventData.account_plan = actionData.account_plan;
    }
    if (actionData.account_type !== undefined) {
      this.eventData.account_type = actionData.account_type;
    }
    if (actionData.name !== undefined) {
      this.eventData.name = actionData.name;
    }
    if (actionData.phone !== undefined) {
      this.eventData.phone = actionData.phone;
    }
    if (actionData.account_id !== undefined) {
      this.eventData.account_id = actionData.account_id;
    }
    if (actionData.username !== undefined) {
      this.eventData.username = actionData.username;
    }
    if (actionData.domain !== undefined) {
      // We do not want to pass domain on the initial "signup" event because it is not actually the domain
      // but instead the org name passed in by the signup form.
      // Additionally, we want to normalize the domain to always include .onsip.com
      let domain = actionData.domain;
      if (!domain.endsWith(".onsip.com")) {
        domain = domain + ".onsip.com";
      }
      this.eventData.domain = domain;
    }
    if (actionData.content_management_system !== undefined) {
      this.eventData.content_management_system = actionData.content_management_system;
    }
    if (actionData.user_id !== undefined) {
      this.eventData.user_id = actionData.user_id;
    }
    if (actionData.organization_id !== undefined) {
      this.eventData.organization_id = actionData.organization_id;
    }
    if (actionData.touched !== undefined) {
      this.eventData.touched = actionData.touched;
    }

    debug && console.warn("Event data:", this.eventData);
    this.tracker.trackEvent("signup", this.eventData);
  }
}
